import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AlertCircle, Loader2, Shield } from "lucide-react";
import { GoogleLogin } from "@react-oauth/google";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import PreviewEnvBanner from "../components/PreviewEnvBanner";

export default function Login() {
  const { login, loginWithGoogle, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showAdmin, setShowAdmin] = useState(false);
  const googleEnabled = !!process.env.REACT_APP_GOOGLE_CLIENT_ID;

  useEffect(() => {
    if (user) navigate(from, { replace: true });
  }, [user, from, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email.trim(), password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Welcome back");
      navigate(from, { replace: true });
    } else {
      setError(res.error || "Login failed");
    }
  };

  const onGoogle = async (credentialResponse) => {
    setError("");
    setSubmitting(true);
    const res = await loginWithGoogle(credentialResponse.credential);
    setSubmitting(false);
    if (res.ok) {
      toast.success(`Welcome, ${res.user.name}`);
      navigate(from, { replace: true });
    } else {
      setError(res.error || "Google sign-in failed");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FFFBEB]">
      <PreviewEnvBanner />
      <div className="flex-1 grid lg:grid-cols-[1.1fr,1fr]">
        {/* Left: editorial visual */}
      <div className="relative hidden lg:block overflow-hidden bg-[#0A1418]">
        <img
          src="/brand/tank-stack.jpeg"
          alt="ISO tank containers stacked at the MTG depot, Port of Tema"
          className="absolute inset-0 w-full h-full object-cover opacity-65"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#003147]/95 via-[#003147]/55 to-transparent" />

        <div className="relative h-full p-12 xl:p-14 flex flex-col justify-between text-white">
          {/* Header */}
          <div className="flex items-center gap-3">
            <img src="/brand/portside-logo.svg" alt="Portside" className="h-12 w-auto" />
            <div className="border-l border-white/30 pl-3 ml-1">
              <div className="font-display text-lg font-extrabold tracking-tight text-white leading-none">
                Portside
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/70 mt-1">
                MTG · Operations Console
              </div>
            </div>
          </div>

          {/* Hero copy */}
          <div className="space-y-5 max-w-lg">
            <div className="ps-rule" />
            <div className="inline-flex items-center gap-2 px-2.5 py-1 border border-white/30 rounded-full backdrop-blur-sm bg-white/5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#99D420]" />
              <span className="text-[10px] font-mono uppercase tracking-widest">Multi Purpose Terminal Ghana · v1</span>
            </div>
            <h2 className="font-display text-4xl xl:text-5xl font-black leading-[1.05] tracking-tight">
              Every tank, every truck, every gate&nbsp;move —
              <br />
              <span className="italic font-medium text-[#FEE685]">accounted for.</span>
            </h2>
            <p className="text-sm xl:text-base text-white/85 leading-relaxed max-w-md">
              The operations console for MTG at the Port of Tema. From gate IN
              to CODECO 95B — the same standards of clarity and reliability that
              Portside is built on, in one terminal.
            </p>
          </div>

          {/* Metrics strip */}
          <div className="grid grid-cols-3 gap-4 max-w-lg text-xs">
            {[
              { k: "Gate Events", v: "Tracked", c: "#99D420" },
              { k: "CODECO 95B", v: "Generated", c: "#009FE3" },
              { k: "ISO Tank Wash", v: "Certified", c: "#F1471D" },
            ].map((m) => (
              <div key={m.k} className="border-t border-white/20 pt-3">
                <div className="flex items-center gap-1.5">
                  <span className="w-1 h-1 rounded-full" style={{ background: m.c }} />
                  <div className="text-[10px] uppercase tracking-widest text-white/70">{m.k}</div>
                </div>
                <div className="font-display text-base font-bold text-white mt-1">{m.v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 sm:p-12 ps-grid-bg">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-3 lg:hidden mb-10">
            <img src="/brand/portside-logo.svg" alt="Portside" className="h-10 w-auto" />
            <div>
              <div className="font-display text-lg font-extrabold tracking-tight text-[#1A1410] leading-none">Portside</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#5A4E42] mt-0.5">MTG · Operations</div>
            </div>
          </div>

          <div className="mb-8">
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#5A4E42] mb-2 font-mono">
              Operations sign-in
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-[#1A1410]">
              Welcome to the terminal.
            </h1>
            <p className="text-sm text-[#5A4E42] mt-2 leading-relaxed">
              Sign in with your <span className="font-mono">@portside.nl</span> Google account.
              Sessions expire after 15 minutes of inactivity.
            </p>
          </div>

          {error && (
            <div
              className="mb-4 flex items-start gap-2 border border-[#F5C1B9] bg-[#FCE8E5] px-3 py-2 rounded-md text-sm text-[#7F1D1D]"
              data-testid="login-error"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {googleEnabled && (
            <div className="space-y-3" data-testid="google-signin-block">
              <div className="flex justify-center">
                <GoogleLogin
                  onSuccess={onGoogle}
                  onError={() => setError("Google sign-in failed. Please try again.")}
                  theme="filled_blue"
                  size="large"
                  shape="rectangular"
                  text="signin_with"
                  width="380"
                />
              </div>
              {submitting && (
                <div className="flex items-center justify-center gap-2 text-sm text-[#5A4E42]">
                  <Loader2 className="w-4 h-4 animate-spin" /> Signing in…
                </div>
              )}
            </div>
          )}

          {!showAdmin ? (
            <div className="mt-8 text-center">
              <button
                type="button"
                onClick={() => setShowAdmin(true)}
                className="inline-flex items-center gap-1.5 text-xs text-[#8C7D6D] hover:text-[#003147] transition-colors underline-offset-4 hover:underline"
                data-testid="show-admin-login"
              >
                <Shield className="w-3 h-3" />
                Admin sign-in (email &amp; password)
              </button>
            </div>
          ) : (
            <>
              <div className="mt-8 mb-4 flex items-center gap-3">
                <div className="h-px bg-[#E5E0D2] flex-1" />
                <span className="text-[10px] uppercase tracking-[0.18em] font-mono text-[#8C7D6D]">Admin break-glass</span>
                <div className="h-px bg-[#E5E0D2] flex-1" />
              </div>

              <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.18em] text-[#5A4E42] mb-1.5">
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    placeholder="you@portside.nl"
                    className="ps-input font-mono"
                    data-testid="login-email-input"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.18em] text-[#5A4E42] mb-1.5">
                    Password
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    placeholder="••••••••"
                    className="ps-input font-mono"
                    data-testid="login-password-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="ps-btn ps-btn-primary w-full h-11 text-sm"
                  data-testid="login-submit-button"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</>
                  ) : (
                    "Sign in"
                  )}
                </button>
              </form>
            </>
          )}

          <div className="mt-10 flex items-center justify-between text-[10px] text-[#8C7D6D] uppercase tracking-[0.18em] font-mono">
            <span>Portside Holding</span>
            <span>Port of Tema · Ghana</span>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
